import { useEffect, useState } from 'react';
import { useColorScheme } from '@mui/material';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import { decodeJWT } from 'aws-amplify/auth';

import { useAuthContext } from 'src/context/AuthContext';
import AuthUtils from 'src/utils/AuthUtils';
import { useSettingsContext } from 'src/components/settings';
import EncryptUtils from 'src/utils/EncryptUtils';
import { getBannerDetails, getModuleListData } from 'src/services/service-handlers/PrivateService';
import { SplashScreen } from 'src/components/loading-screen';
import { useBannerDetails } from 'src/context/BannerContext';

import { authRoutes } from './auth';
import { mainRoutes } from './main';
import { getUserRoutes } from './user';
import { getAdminRoutes } from './admin';

// ----------------------------------------------------------------------

export function PublicRouter() {
  return useRoutes([
    // Auth
    ...authRoutes,

    // Dashboard
    // ...dashboardRoutes,

    // Main
    ...mainRoutes,

    // Components
    // ...componentsRoutes,

    // No match
    { path: '/', element: <Navigate to="/sign-in" replace /> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

export function PrivateRouter() {
  const location = useLocation();
  const settings = useSettingsContext();
  const { setBannerDetails } = useBannerDetails();
  const { mode, setMode } = useColorScheme();
  const { authState, isAdminPortal } = useAuthContext();
  const { isAdmin } = authState;

  const token = AuthUtils.getAuthToken();
  const [decodedToken, setDecodedToken] = useState<any>(null);
  const [bannerData, setBannerData] = useState({});
  const [adminCheckDone, setAdminCheckDone] = useState(false);
  const [moduleList, setModuleList] = useState([]);
  const [modulesFetched, setModulesFetched] = useState(false);

  // Decode token
  useEffect(() => {
    const decode = async () => {
      try {
        const decoded = await decodeJWT(token);
        setDecodedToken(decoded);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    };
    if (token) decode();
  }, [token]);

  // Prevent back button
  useEffect(() => {
    const preventBack = () => {
      window.history.pushState(null, document.title, window.location.href);
    };
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener('popstate', preventBack);
    return () => window.removeEventListener('popstate', preventBack);
  }, []);

  // Admin check
  useEffect(() => {
    if (!decodedToken) return;

    if (!decodedToken?.payload?.user) {
      isAdminPortal({ isAdmin: true });
    }
    setAdminCheckDone(true);
  }, [decodedToken, isAdminPortal]);

  // Fetch banner
  useEffect(() => {
    if (!decodedToken?.payload?.user?.PersonId) return;

    const fetchBannerData = async () => {
      const payload = { PersonId: decodedToken.payload.user.PersonId };
      console.log(payload, decodedToken.payload, 'payload');
      const encryptedPayload = EncryptUtils.encryptedData(payload);
      try {
        const data = await getBannerDetails({ tk: encryptedPayload });
        setBannerData(data);
        setBannerDetails(data);
      } catch (error) {
        console.error('Failed to fetch banner data:', error);
      }
    };

    fetchBannerData();
  }, [decodedToken, setBannerDetails]);

  // Fetch modules
  useEffect(() => {
    if (!decodedToken) return;

    const fetchModules = async () => {
      try {
        const role = decodedToken.payload?.user ? 'user' : 'admin';
        const modules = await getModuleListData('park-city', role);
        setModuleList(modules);
        setModulesFetched(true);
      } catch (error) {
        console.error('Failed to fetch module list:', error);
        setModulesFetched(false);
      }
    };

    fetchModules();
  }, [decodedToken]);

  // Use placeholder route while loading
  const loadingRoutes = [
    {
      path: '*',
      element: <SplashScreen />,
    },
  ];

  // Build actual routes only when ready
  const routes =
    decodedToken && adminCheckDone && modulesFetched
      ? [
          ...(isAdmin
            ? getAdminRoutes(
                location.pathname,
                decodedToken,
                settings,
                mode,
                setMode,
                bannerData,
                moduleList
              )
            : getUserRoutes(
                location.pathname,
                decodedToken,
                settings,
                mode,
                setMode,
                bannerData,
                moduleList
              )),
          ...mainRoutes,
          {
            path: '/',
            element: <Navigate to='/profile' replace />,
          },
          // { path: '*', element: <Navigate to="/404" replace /> },
        ]
      : loadingRoutes;

  return useRoutes(routes);
}
